import React, { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../store/alertSlice';
import PasswordInput from '../ui/PasswordInput';

const ForgotPassword = ({ onClose }) => {
  const dispatch = useDispatch();
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStage, setForgotStage] = useState('idle');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    paddingLeft: '38px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: '#f9fafb',
    color: '#1f2937',
  };

  const handleSendCode = async () => {
    if (!forgotEmail) {
      setForgotError('Please enter your email before requesting a code.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    setForgotMessage('');

    try {
      await resetPassword({ username: forgotEmail });
      setForgotStage('codeSent');
      setForgotMessage('Verification code sent to your email.');
    } catch (err) {
      setForgotError(err.message || 'Failed to send verification code.');
      console.error('Forgot password error:', err);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleReset = async () => {
    if (!resetCode || !newPassword) {
      setForgotError('Enter the verification code and your new password.');
      return;
    }
    if (newPassword.length < 8) {
      setForgotError('Password must be at least 8 characters.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    setForgotMessage('');

    try {
      await confirmResetPassword({ username: forgotEmail, confirmationCode: resetCode, newPassword: newPassword });
      dispatch(showAlert({
        message: 'Password reset successfully! You can now sign in with your new password.',
        type: 'success'
      }));
      onClose();
      // reset fields
      setForgotEmail('');
      setForgotStage('idle');
      setResetCode('');
      setNewPassword('');
    } catch (err) {
      setForgotError(err.message || 'Failed to reset password.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div style={{
      marginTop: '12px',
      padding: '16px',
      borderRadius: '10px',
      border: '1px solid #d1d5db',
      background: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
          Reset your password
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#2563eb',
            cursor: 'pointer',
            fontSize: '12px',
            textDecoration: 'underline',
            padding: 0
          }}
        >
          Back to sign in
        </button>
      </div>

      {(forgotMessage || forgotError) && (
        <div style={{ marginTop: '16px' }}>
          {forgotMessage && (
            <div style={{
              marginBottom: '8px',
              padding: '10px',
              borderRadius: '6px',
              background: '#ecfeff',
              border: '1px solid #a5f3fc',
              color: '#0f766e',
              fontSize: '14px'
            }}>
              {forgotMessage}
            </div>
          )}
          {forgotError && (
            <div style={{
              padding: '10px',
              borderRadius: '6px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              fontSize: '14px'
            }}>
              {forgotError}
            </div>
          )}
        </div>
      )}

      {forgotStage === 'idle' && (
        <>
          <div style={{ fontSize: '13px', color: '#475569' }}>
            Enter your email below and we will send a verification code.
          </div>
          <div style={{ position: 'relative' }}>
            <label htmlFor="forgotEmail" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#334155' }}>
              Email
            </label>
            <span style={{ position: 'absolute', left: '12px', top: '34px', color: '#9ca3af' }}>✉️</span>
            <input
              id="forgotEmail"
              type="email"
              placeholder="email@company.com"
              value={forgotEmail}
              onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
              style={inputStyle}
              disabled={forgotLoading}
            />
          </div>
          <button
            type="button"
            onClick={handleSendCode}
            disabled={forgotLoading}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid #2563eb',
              background: forgotLoading ? '#9ca3af' : 'white',
              color: forgotLoading ? 'white' : '#2563eb',
              fontWeight: '600',
              cursor: forgotLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {forgotLoading ? 'Sending code...' : 'Send verification code'}
          </button>
        </>
      )}

      {forgotStage === 'codeSent' && (
        <>
          <div style={{ position: 'relative' }}>
            <label htmlFor="resetCode" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#334155' }}>
              Verification code
            </label>
            <span style={{ position: 'absolute', left: '12px', top: '34px', color: '#9ca3af' }}>🔐</span>
            <input
              id="resetCode"
              type="text"
              placeholder="Enter verification code"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              style={inputStyle}
              disabled={forgotLoading}
            />
          </div>
          <PasswordInput
            id="newPassword"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password"
            disabled={forgotLoading}
            labelStyle={{ fontSize: '12px', marginBottom: '6px', color: '#334155' }}
            style={{ fontSize: '14px' }}
          />
          <button
            type="button"
            onClick={handleReset}
            disabled={forgotLoading}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: 'none',
              background: forgotLoading ? '#9ca3af' : '#2563eb',
              color: 'white',
              fontWeight: '600',
              cursor: forgotLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {forgotLoading ? 'Resetting password...' : 'Reset password'}
          </button>
        </>
      )}
    </div>
  );
};

export default ForgotPassword;